import { useEffect, useRef, type RefObject } from 'react';

export interface InfiniteScrollOptions {
  // Observe only while there is something left to fetch and nothing in flight;
  // this is what stops a rested-at-the-bottom sentinel firing repeatedly.
  readonly enabled: boolean;
  readonly onLoadMore: () => void;
  readonly rootMargin?: string;
}

// Fires onLoadMore when the referenced element scrolls into view. Loads the
// next page slightly before the sentinel is actually visible so the list feels
// continuous rather than stepping. Degrades to no auto-loading where
// IntersectionObserver is unavailable — the manual control stays the fallback.
export const useInfiniteScroll = (
  target: RefObject<HTMLElement | null>,
  { enabled, onLoadMore, rootMargin = '240px' }: InfiniteScrollOptions,
): void => {
  const onLoadMoreRef = useRef(onLoadMore);
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const node = target.current;
    if (node === null) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onLoadMoreRef.current();
      },
      { rootMargin },
    );
    observer.observe(node);
    return (): void => {
      observer.disconnect();
    };
  }, [target, enabled, rootMargin]);
};
